import { Controller } from "@hotwired/stimulus"
import flash from "../lib/flashes.js"
import tippy from "tippy.js"

export default class extends Controller {
  static targets = ["dropZone", "input", "defaultState", "loadingState", "currentFile", "totalFiles"]

  connect() {
    this.dropZone = this.dropZoneTarget
    this.boundHandlePaste = this.handlePaste.bind(this)
    this.bindEvents()
  }

  disconnect() {
    // Clean up the global paste event listener when controller is destroyed
    document.removeEventListener('paste', this.boundHandlePaste)
  }

  bindEvents() {
    // Prevent default drag behaviors
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      this.dropZone.addEventListener(eventName, this.preventDefaults, false)
      document.body.addEventListener(eventName, this.preventDefaults, false)
    })

    this.dropZone.addEventListener('click', this.openFileDialog.bind(this))
    this.dropZone.addEventListener('dragover', this.handleDragOver.bind(this))
    this.dropZone.addEventListener('dragleave', this.handleDragLeave.bind(this))
    this.dropZone.addEventListener('drop', this.handleDrop.bind(this))

    // Add paste event listener to the document with stored reference for cleanup
    document.addEventListener('paste', this.boundHandlePaste)
  }

  preventDefaults(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  openFileDialog(event) {
    event.preventDefault()
    this.inputTarget.click()
  }

  handleDragOver(event) {
    this.preventDefaults(event)
    this.dropZone.classList.add('drag-over')
  }

  handleDragLeave(event) {
    this.preventDefaults(event)
    this.dropZone.classList.remove('drag-over')
  }

  handleDrop(event) {
    this.preventDefaults(event)
    this.dropZone.classList.remove('drag-over')
    
    // Check if we have files
    const files = event.dataTransfer.files
    if (files && files.length > 0) {
      this.uploadFiles(files)
      return
    }
    
    // If no files, try to get them from items (similar to paste handling)
    if (event.dataTransfer.items) {
      const itemFiles = []
      
      for (const item of event.dataTransfer.items) {
        if (item.kind === 'file' && item.type.indexOf('image') === 0) {
          const file = item.getAsFile()
          if (file) {
            itemFiles.push(file)
          }
        }
      }
      
      if (itemFiles.length > 0) {
        this.uploadFiles(itemFiles)
        return
      }
    }
    
    // Handle URL-based drops (images from web pages)
    if (event.dataTransfer.types.includes('text/uri-list')) {
      const url = event.dataTransfer.getData('text/uri-list')
      
      // Check for CID URLs (Outlook embedded images)
      if (url.startsWith('cid:')) {
        flash("Embedded images cannot be dragged directly. Please save the image first or use copy/paste (Ctrl+C, Ctrl+V) instead.", "error")
        return
      }
      
      // Check for Gmail URLs (require authentication)
      if (url.includes('mail.google.com')) {
        flash("The image cannot be dragged directly due to authentication requirements. Please save the image first or use copy/paste (Ctrl+C, Ctrl+V) instead.", "error")
        return
      }
      
      this.handleUrlDrop(event)
      return
    }
  }

  async handleUrlDrop(event) {
    try {
      // Get the URL from the drop event
      const url = event.dataTransfer.getData('text/uri-list')
      
      if (!url) {
        return
      }
      
      this.showLoadingState()
      
      const response = await this.uploadImageFromUrl(url)
      const jsonResponse = await response.json()
      this.hideLoadingState()
      
      if (response.ok && jsonResponse.success === true) {
        this.handleSuccessfulUpload()
      } else {
        this.handleFailedUpload(jsonResponse)
      }
      
    } catch (error) {
      this.handleUploadError(error)
    }
  }

  showLoadingState() {
    this.defaultStateTarget.classList.add('hidden')
    this.loadingStateTarget.classList.remove('hidden')
    this.totalFilesTarget.textContent = '1'
    this.currentFileTarget.textContent = '1'
  }

  hideLoadingState() {
    this.defaultStateTarget.classList.remove('hidden')
    this.loadingStateTarget.classList.add('hidden')
  }

  async uploadImageFromUrl(url) {
    const formData = this.createFormDataForUrl(url)
    
    return fetch('/photos/fetch_from_url', {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'application/json'
      },
      body: formData
    })
  }

  createFormDataForUrl(url) {
    const formData = new FormData()
    formData.append('url', url)
    formData.append('company_id', this.element.dataset.companyId)
    formData.append('photoable_type', this.element.dataset.photoableType)
    formData.append('photoable_id', this.element.dataset.photoableId)
    
    if (this.element.dataset.folderId) {
      formData.append('folder_id', this.element.dataset.folderId)
    }
    
    return formData
  }

  handleSuccessfulUpload() {
    flash("Photo uploaded successfully", "success")
    this.refreshPageContent()
  }

  refreshPageContent() {
    const pageUrl = new URL(window.location.href)
    const photosContainer = document.querySelector('.photos__container')
    const sidebarContainer = document.querySelector('.asset-sidebar__group-content--Photos')
    const containerToUpdate = photosContainer || sidebarContainer
    
    if (containerToUpdate) {
      fetch(pageUrl)
        .then(response => response.text())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html')
          this.updateSidebarCount()
          this.updateContainers(doc, photosContainer, sidebarContainer)
        })
    }
  }

  updateSidebarCount() {
    const sidebarLinks = document.querySelectorAll('.sidebar__link')
    sidebarLinks.forEach(link => {
      const label = link.querySelector('.sidebar__link-name')
      const count = link.querySelector('.count')
      
      if (label && label.textContent.trim() === 'Photos' && count) {
        const current = parseInt(count.textContent, 10)
        if (!isNaN(current)) {
          count.textContent = current + 1
        }
      }
    })
  }

  updateContainers(doc, photosContainer, sidebarContainer) {
    if (photosContainer) {
      const newPhotosContainer = doc.querySelector('.photos__container')
      if (newPhotosContainer) {
        photosContainer.innerHTML = newPhotosContainer.innerHTML
      }
    }
    
    if (sidebarContainer) {
      const newSidebarContainer = doc.querySelector('.asset-sidebar__group-content--Photos')
      if (newSidebarContainer) {
        sidebarContainer.innerHTML = newSidebarContainer.innerHTML
      }
    }
  }

  handleFailedUpload(jsonResponse) {
    const errorMessage = jsonResponse.errors ? jsonResponse.errors.join(', ') : jsonResponse.error || 'Unknown error'
    flash(`Failed to upload image: ${errorMessage}`, "error")
  }

  handleUploadError(error) {
    this.hideLoadingState()
    flash(`Failed to upload image: ${error.message}`, "error")
  }

  handleFileSelect(event) {
    const files = event.target.files
    this.uploadFiles(files)
  }

  handlePaste(event) {
    // Don't intercept paste if user is typing in a text input, textarea, or contenteditable
    // This allows normal text paste (e.g., from Excel) to work in search inputs
    const activeElement = document.activeElement
    const textInputTypes = [
      'text',
      'search',
      'email',
      'url',
      'tel',
      'password',
    ]
    
    const isEditableElement = activeElement && (
      (activeElement.tagName === 'INPUT' &&
        textInputTypes.includes(activeElement.type)) ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.isContentEditable
    )

    if (isEditableElement) {
      return
    }

    const items = (event.clipboardData || event.originalEvent.clipboardData).items
    const files = []

    for (const item of items) {
      if (item.type.indexOf('image') === 0) {
        const file = item.getAsFile()
        files.push(file)
      }
    }

    if (files.length > 0) {
      event.preventDefault()
      this.uploadFiles(files)
    }
  }

  async uploadFiles(files) {
    
    this.defaultStateTarget.classList.add('hidden')
    this.loadingStateTarget.classList.remove('hidden')

    this.totalFilesTarget.textContent = files.length
    let currentFileCount = 0
    let errors = []

    for (const file of files) {
      currentFileCount++
      this.currentFileTarget.textContent = currentFileCount

      const formData = new FormData()
      formData.append('file', file)
      formData.append('company_id', this.element.dataset.companyId)
      formData.append('photoable_type', this.element.dataset.photoableType)
      formData.append('photoable_id', this.element.dataset.photoableId)

      if (this.element.dataset.folderId) {
        formData.append('folder_id', this.element.dataset.folderId)
      }

      try {
        const response = await fetch('/photos', {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
            'Accept': 'application/json'
          },
          body: formData
        })

        const jsonResponse = await response.json()

        if (response.ok && jsonResponse.success) {
          // Successfully uploaded single file, continue loop
        } else {
          errors.push({
            filename: file.name,
            messages: jsonResponse.errors || ["Unknown error."]
          });
        }
      } catch (error) {
        errors.push({
          filename: file.name,
          messages: [error.message]
        });
      }
    }

    // Hide loading state after all uploads complete
    this.defaultStateTarget.classList.remove('hidden')
    this.loadingStateTarget.classList.add('hidden')

    // Handle Toast Messages:
    if (errors.length === 0) {
      flash("Photos uploaded successfully", "success");
    } else {
      errors.forEach(err => {
        flash(`Failed to upload ${err.filename}: ${err.messages.join(", ")}`, "error");
      });
    }

    // Refresh the appropriate container based on context
    const url = new URL(window.location.href)
    const photosContainer = document.querySelector('.photos__container')
    const sidebarContainer = document.querySelector('.asset-sidebar__group-content--Photos')
    const containerToUpdate = photosContainer || sidebarContainer

    if (containerToUpdate) {
      fetch(url)
        .then(response => response.text())
        .then(html => {
          const parser = new DOMParser()
          const doc = parser.parseFromString(html, 'text/html')

          // Reinitialize Tippy tooltips
          tippy('[data-tippy-content]')

          // Update sidebar count
          const sidebarLinks = document.querySelectorAll('.sidebar__link')
          sidebarLinks.forEach(link => {
            const label = link.querySelector('.sidebar__link-name')
            const count = link.querySelector('.count')

            if (label && label.textContent.trim() === 'Photos' && count) {
              const current = parseInt(count.textContent, 10)
              const uploadedCount = files.length
              if (!isNaN(current)) {
                count.textContent = current + uploadedCount
              }
            }
          })

          // Replace containers
          if (photosContainer) {
            const newPhotosContainer = doc.querySelector('.photos__container')
            if (newPhotosContainer) {
              photosContainer.innerHTML = newPhotosContainer.innerHTML

              // Force Stimulus to reload the bulk selector and floating action menu controllers in the updated container so they have updated states
              const bulkSelectorController = this.application.getControllerForElementAndIdentifier(photosContainer, 'bulk-selector')
              const floatingMenuController = this.application.getControllerForElementAndIdentifier(photosContainer, 'floating-action-menu')
              
              if (bulkSelectorController) {
                bulkSelectorController.disconnect()
                bulkSelectorController.connect()
              }
              
              if (floatingMenuController) {
                floatingMenuController.disconnect()
                floatingMenuController.connect()
              }
            }
          }

          if (sidebarContainer) {
            const newSidebarContainer = doc.querySelector('.asset-sidebar__group-content--Photos')
            if (newSidebarContainer) {
              sidebarContainer.innerHTML = newSidebarContainer.innerHTML
            }
          }
        })
    }
  }
}